import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';

const products = [
  { name: 'Standard Pallet Racking', path: '/products/standard-pallet-racking', desc: 'Cost-effective organized storage' },
  { name: 'Adjustable Pallet Racking', path: '/products/adjustable-pallet-racking', desc: 'Flexible beam heights' },
  { name: 'Drive-In Pallet Racking', path: '/products/drive-in-racking', desc: 'High-density LIFO storage' },
  { name: 'Shuttle-Based Racking', path: '/products/shuttle-racking', desc: 'Semi-automated deep lane' },
  { name: 'ASRS Based Racking', path: '/products/asrs-racking', desc: 'Fully automated systems' },
  { name: 'Mezzanine Floors', path: '/products/mezzanine-floors', desc: 'Multi-level structures' },
];

const industries = [
  { name: 'Consumer Goods & Electronics', path: '/industries/consumer-goods' },
  { name: 'Automotive', path: '/industries/automotive' },
  { name: 'Food & Beverages', path: '/industries/food-beverage' },
  { name: '3PL & Logistics', path: '/industries/3pl-logistics' },
  { name: 'E & Q Commerce', path: '/industries/ecommerce' },
  { name: 'Pharmaceuticals', path: '/industries/pharmaceuticals' },
  { name: 'Semi Conductors', path: '/industries/semiconductors' },
  { name: 'Manufacturing', path: '/industries/manufacturing' },
  { name: 'Aerospace / Aviation', path: '/industries/aerospace' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const navLinkClass = "relative text-[13px] text-gray-400 hover:text-white transition-colors uppercase tracking-[0.15em] font-medium py-2";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass border-b border-white/5 py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo">
            <div className="bg-white rounded px-2 py-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/vjaqzlc7_IMG_0255.jpeg" 
                alt="Warehaus+ - Designed to Store. Built to Scale."
                className="h-8 md:h-10 w-auto object-contain group-hover:opacity-90 transition-opacity"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className={navLinkClass} data-testid="nav-home">
              Home
            </Link>
            <Link to="/about" className={navLinkClass} data-testid="nav-about">
              About Us
            </Link>
            <Link to="/why-warehaus" className={navLinkClass} data-testid="nav-why">
              Why Warehaus+
            </Link>

            {/* Products Dropdown */}
            <div className="relative nav-item group">
              <button className={`${navLinkClass} flex items-center gap-1.5`} data-testid="nav-products">
                Products
                <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="nav-dropdown absolute top-full left-1/2 -translate-x-1/2 pt-6">
                <div className="glass-card bg-[#0a1628]/95 border border-white/5 p-6 min-w-[420px] shadow-2xl">
                  <div className="grid grid-cols-2 gap-2">
                    {products.map((product) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        className="group/item p-3 hover:bg-white/5 transition-colors rounded-sm"
                        data-testid={`nav-product-${product.path.split('/').pop()}`}
                      >
                        <p className="text-sm text-white font-medium group-hover/item:text-[#E63946] transition-colors">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">{product.desc}</p>
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-white/5 mt-4 pt-4">
                    <Link
                      to="/products"
                      className="flex items-center justify-between p-3 bg-[#E63946]/10 border border-[#E63946]/20 hover:border-[#E63946]/40 transition-colors group/all"
                      data-testid="nav-all-products"
                    >
                      <span className="text-sm text-[#E63946] font-medium">View All Products</span>
                      <ArrowRight className="w-4 h-4 text-[#E63946] group-hover/all:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Link to="/services" className={navLinkClass} data-testid="nav-services">
              Services
            </Link>

            {/* Industries Dropdown */}
            <div className="relative nav-item group">
              <button className={`${navLinkClass} flex items-center gap-1.5`} data-testid="nav-industries">
                Industries
                <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="nav-dropdown absolute top-full right-0 pt-6">
                <div className="glass-card bg-[#0a1628]/95 border border-white/5 p-6 min-w-[320px] shadow-2xl">
                  <div className="grid grid-cols-1 gap-1">
                    {industries.map((industry) => (
                      <Link
                        key={industry.path}
                        to={industry.path}
                        className="group/item flex items-center justify-between px-4 py-2.5 hover:bg-white/5 transition-colors rounded-sm"
                        data-testid={`nav-industry-${industry.path.split('/').pop()}`}
                      >
                        <span className="text-sm text-gray-400 group-hover/item:text-white transition-colors">
                          {industry.name}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-white/5 mt-4 pt-4">
                    <Link
                      to="/industries"
                      className="flex items-center justify-between p-3 bg-[#E63946]/10 border border-[#E63946]/20 hover:border-[#E63946]/40 transition-colors group/all"
                      data-testid="nav-all-industries"
                    >
                      <span className="text-sm text-[#E63946] font-medium">View All Industries</span>
                      <ArrowRight className="w-4 h-4 text-[#E63946] group-hover/all:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Link to="/contact" className={navLinkClass} data-testid="nav-contact">
              Contact
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contact" data-testid="nav-contact-btn">
              <Button className="btn-gold magnetic-btn clip-slant h-11 px-7 text-xs">
                Contact Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 hover:bg-white/5 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mobile-menu-enter bg-[#0a1628] border-t border-white/5 py-6" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider">
                Home
              </Link>
              <Link to="/about" className="px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider">
                About Us
              </Link>
              <Link to="/why-warehaus" className="px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider">
                Why Warehaus+
              </Link>

              {/* Products Mobile */}
              <button
                className="flex items-center justify-between px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider"
                onClick={() => toggleDropdown('products')}
              >
                Products
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'products' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'products' && (
                <div className="bg-[#0c1e35] py-2">
                  {products.map((product) => (
                    <Link
                      key={product.path}
                      to={product.path}
                      className="block px-8 py-2.5 text-sm text-gray-500 hover:text-white transition-colors"
                    >
                      {product.name}
                    </Link>
                  ))}
                </div>
              )}

              <Link to="/services" className="px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider">
                Services
              </Link>

              {/* Industries Mobile */}
              <button
                className="flex items-center justify-between px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider"
                onClick={() => toggleDropdown('industries')}
              >
                Industries
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'industries' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'industries' && (
                <div className="bg-[#0c1e35] py-2">
                  {industries.map((industry) => (
                    <Link
                      key={industry.path}
                      to={industry.path}
                      className="block px-8 py-2.5 text-sm text-gray-500 hover:text-white transition-colors"
                    >
                      {industry.name}
                    </Link>
                  ))}
                </div>
              )}

              <Link to="/contact" className="px-4 py-3.5 text-gray-300 hover:text-white hover:bg-white/5 transition-colors text-sm uppercase tracking-wider">
                Contact
              </Link>

              <div className="px-4 pt-6">
                <Link to="/contact" className="block">
                  <Button className="btn-gold clip-slant w-full h-12 text-sm">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
